<script lang="ts" setup>
// Vueuse
import { vElementHover } from '@vueuse/components'

import { ROUTE_NETWORK } from '~/contexts/network/router/routes'

import { useCommunityCreation } from '~/entities/container/community/composables/acl-community-creation'

import { useContextManager } from '~/features-shared/config-manager/composables/context-manager'
import { EContext } from '~/features-shared/config-manager/interfaces/contexts'
import NavigationBar from '~/features-shared/left-panel/components/navigation-bar.vue'
import {
  currentContext,
  isPanelActive,
  isPanelOpened,
  isPanelPinned,
  toggleActiveContext,
  togglePinContext,
  useLeftPanelManager,
} from '~/features-shared/left-panel/composables/left-panel-manager'

import { useFeatureManager } from '~/features-generic/config-manager/composables/feature-manager'
import { hasAccessTo } from '~/features-generic/config-manager/composables/page-manager'
import LeftPanel from '~/features-generic/left-panel/components/left-panel.vue'

defineProps<{
  /**
   * What layout should use the component ?
   * - left : display the navigation bar on left side of the viewport.
   *  The panel will have active/open state when the user navigate on navbar items.
   * - bottom : display the navigation bar on bottom side of the viewport.
   *  The panel will be always visible
   */
  layout: 'left' | 'bottom'
  /**
   * Hide the left panel ? Set true to hide it.
   * It will not hide the navigation bar !
   *
   * Some pages do not need/do not have a left panel
   *
   * @default false
   */
  hideLeftPanel?: boolean
}>()

const isMobileBreakpoint = useFeatureManager('app.config.mobile_breakpoint')
const navBarPositionMode = useFeatureManager('layout.nav_bar_position')
const isNavBarActivated = useFeatureManager('layout.nav_bar')
const isLeftColumnActivated = useFeatureManager('layout.left_column')

const { searchVal, selected } = useLeftPanelManager()

const { context: navigationContext } = useContextManager()

const { shouldShowCreateCommunityIcon } = useCommunityCreation()

function onHover(hover: boolean) {
  toggleActiveContext(hover ? currentContext.value : 0)
}

const LeftPanelBodyCommunity = defineAsyncComponent(
  () => import('~/features-swym/community/components/left-panel-body-community.vue'),
)
const LeftPanelBodyDm = defineAsyncComponent(
  () => import('~/features-shared/conversation/components/left-panel-body-dm.vue'),
)
const LeftPanelBodyAURA = defineAsyncComponent(
  () => import('~/features-swym/aura/components/left-panel-body-aura.vue'),
)
const LeftPanelBodyCalls = defineAsyncComponent(
  () => import('~/features-swym/calls/components/left-panel-body-calls.vue'),
)
const LeftPanelBodyRitual = defineAsyncComponent(
  () => import('~/features-shared/ritual/components/left-panel-body-ritual.vue'),
)
const LeftPanelBodyNetwork = defineAsyncComponent(
  () => import('~/features-swym/network/components/left-panel-body-network.vue'),
)
const LeftPanelBodyMe = defineAsyncComponent(
  () => import('~/features-swym/me/components/left-panel-body-me.vue'),
)

onMounted(() => {
  // if the navbar is disabled (HBM scenario), then do not fetch communities/dms
  if (!isNavBarActivated.value)
    return

  // fetch communities here to get the "unread" counter
  import('~/entities/container/community/stores/communities')
    .then(({ useCommunitiesStore }) => {
      useCommunitiesStore()
        .fetchList()
    })
  // fetch conversations here to get the "unread" counter
  import('~/entities/container/conversation/stores/conversations')
    .then(({ useConversationsStore }) => {
      useConversationsStore()
        .fetchList()
    })

  // fetch aura conversations here to get the "unread" counter
  import('~/features-swym/aura/stores/conversations-aura')
    .then(({ useConversationsAURAStore }) => {
      useConversationsAURAStore()
        .fetchList()
    })

  // fetch network invitations here to get the "unread" counter
  if (hasAccessTo(ROUTE_NETWORK.ACTIVITY)) {
    import('~/features-swym/network-invitation/stores/network-invitations')
      .then(({ useInvitationsStore }) => {
        const storeInvitations = useInvitationsStore()
        if (!storeInvitations.fetchedInvites || currentContext.value === EContext.NETWORK)
          storeInvitations.doFetchNext()
      })
  }
})
</script>

<template>
  <aside
    v-if="isNavBarActivated || isLeftColumnActivated"
    class="left-navigation_container"
    :class="[{
      'mobile': isMobileBreakpoint && navBarPositionMode === 'auto',
      'has-nav-bar': isNavBarActivated && !(isMobileBreakpoint && navBarPositionMode === 'auto'),
    }]"
  >
    <NavigationBar
      v-if="isNavBarActivated"
      class="transition-shadow duration-500 shadow-grey-6"
      :class="[{ 'shadow-xl': isPanelActive && !isPanelPinned }]"
      :compact="layout === 'bottom'"
      :current-context="isMobileBreakpoint ? currentContext : navigationContext"
      :layout="layout"
      :open-context="isPanelOpened ? currentContext : 0"
      @update:context-active="!isMobileBreakpoint && !isPanelPinned && toggleActiveContext($event)"
      @update:context-pin="togglePinContext($event, !isMobileBreakpoint)"
    />
    <transition name="slide">
      <LeftPanel
        v-if="isLeftColumnActivated"
        v-show="(layout === 'bottom' || isPanelOpened) && !hideLeftPanel"
        v-element-hover="onHover"
        class="left-navigation_left-panel"
        :show-icons-constraints="{ [EContext.COMMUNITY]: shouldShowCreateCommunityIcon }"
      >
        <!-- Use keep-alive once they are all migrated :) -->
        <template #body>
          <LeftPanelBodyCommunity
            v-if="currentContext === EContext.COMMUNITY"
            :searched-value="searchVal"
            :selected="selected"
          />
          <LeftPanelBodyDm
            v-else-if="currentContext === EContext.DM"
            :searched-value="searchVal"
            :selected="selected"
          />
          <LeftPanelBodyAURA
            v-else-if="currentContext === EContext.AURA"
            :searched-value="searchVal"
            :selected="selected"
          />
          <LeftPanelBodyMe
            v-else-if="currentContext === EContext.ME"
            :selected="selected"
          />
          <LeftPanelBodyCalls
            v-else-if="currentContext === EContext.CALLS"
            :searched-value="searchVal"
          />
          <LeftPanelBodyRitual
            v-else-if="currentContext === EContext.RITUALS"
            :searched-value="searchVal"
            :selected="selected"
          />
          <LeftPanelBodyNetwork
            v-else-if="currentContext === EContext.NETWORK"
            :searched-value="searchVal"
            :selected="selected"
          />
        </template>
      </LeftPanel>
    </transition>
  </aside>
</template>

<style scoped lang="scss">
.left-navigation_container {
  @apply grid grid-cols-nav-left z-50 w-[83px];

  &:not(.mobile) {
    .left-navigation_left-panel {
      @apply left-0 border-r border-slate-200 absolute w-[var(--side-column-width)] max-w-[100vw] will-change-transform;
    }

    &.has-nav-bar .left-navigation_left-panel {
      @apply left-[83px] w-[calc(var(--side-column-width)_-_83px)] max-w-[calc(100vw_-_83px)]
    }
  }

  &.mobile {
    @apply flex flex-col-reverse w-full;
  }
}
</style>
